import { Waypoint } from 'react-waypoint'
import { useSelector } from 'react-redux'
import { useIntl, defineMessages } from 'react-intl'
import { addMonths, isAfter, startOfMonth } from 'date-fns'
import { Heading } from '@homebotapp/hb-react-component-catalog'
import { AirDnaChart } from '../AirDnaChart'
import { AirDnaSavings } from '../AirDnaSavings'
import { AirDnaChartSliders } from '../AirDnaChartSliders'
import { AirDnaLearnMoreButton } from '../AirDnaLearnMore'
import { AirDnaExtraPayments } from '../AirDnaExtraPayments'
import { DEFAULT_ADDRESS } from '../../../../constants/air_dna'
import { selectRouteKey } from '../../../../store/selectors/routes'
import { useTrackingContext } from '../../../../providers/tracking'
import { selectHasHomeData } from '../../../../store/selectors/home'
import { CorporateProfile } from '../../../../types/corporateProfile'
import { TrackingEventConstants } from '../../../../constants/tracking'
import { useTrackingDebounceSetters } from '../../../../hooks/tracking'
import { selectPaidInFull } from '../../../../store/selectors/home/answers'
import { selectCurrentCELs, selectFirstCEL } from '../../../../store/selectors/home/loans'
import { useCreateHomeClientEventOnce } from 'src/hooks'
import { ClientEventName } from 'src/api/gqlaxy/gql/generated/graphql'
import { useEffect, useState } from 'react'

export const MSG = defineMessages({
  address: {
    id: 'AirDna.rentalQuality.address',
    defaultMessage: 'Showing results for: {address}'
  },
  head: {
    id: 'AirDna.rentalQuality.headline',
    defaultMessage: 'Estimated based on similar rentals nearby'
  }
})

export interface AirDnaBodyProps {
  params: any
  locked: boolean
  stateKey: string
  corporateProfile: CorporateProfile
}

export const AirDnaBody = ({ params, locked, stateKey, corporateProfile }: AirDnaBodyProps) => {
  const intl = useIntl()
  const loans = useSelector(selectCurrentCELs)
  const routeKey = useSelector(selectRouteKey)
  const { trackingClient } = useTrackingContext()
  const paidInFull = useSelector(selectPaidInFull)
  const [hasUsedShortTermRentalCalculator, setHasUsedShortTermRentalCalculator] = useState(false)
  // This ensures we get the correct date when formatting (crazy JS date nuances): https://stackoverflow.com/questions/7556591/is-the-javascript-date-object-always-one-day-off
  const loanOriginationDate = useSelector(selectFirstCEL)?.date?.split('-').join('/')
  const firstPaymentDate = addMonths(startOfMonth(new Date(loanOriginationDate)), 2)
  const hasValidLoan = isAfter(new Date(), firstPaymentDate) && !paidInFull
  const showExtraPayments =
    [routeKey === 'home', useSelector(selectHasHomeData), loans.length].every(Boolean) && hasValidLoan
  const address = params ? params.address : `${DEFAULT_ADDRESS.streetAddress}, ${DEFAULT_ADDRESS.zipcode}`
  const [trackModuleViewed] = useTrackingDebounceSetters(
    () =>
      trackingClient?.moduleViewed({
        module_name: TrackingEventConstants.AIRBNB
      })
  )

  const createUsedShortTermRentalsCalculatorEvent = useCreateHomeClientEventOnce({
    eventName: ClientEventName.UsedShortTermRentalCalculator
  })

  useEffect(() => {
    if (hasUsedShortTermRentalCalculator) {
      createUsedShortTermRentalsCalculatorEvent()
    }
  }, [hasUsedShortTermRentalCalculator, createUsedShortTermRentalsCalculatorEvent])

  return (
    <>
      <Heading as='h3' fontWeight='light' size='sm'>
        {intl.formatMessage(MSG.address, { address: <strong>{address}</strong> })}
      </Heading>
      <Waypoint onEnter={trackModuleViewed} bottomOffset='25%' />
      <AirDnaChart
        locked={locked}
        stateKey={stateKey}
        setHasUsedShortTermRentalCalculator={setHasUsedShortTermRentalCalculator}
      />
      <Heading as='h4' fontWeight='light' size='sm' textAlign='center'>
        {intl.formatMessage(MSG.head)}
      </Heading>
      <AirDnaChartSliders
        stateKey={stateKey}
        setHasUsedShortTermRentalCalculator={setHasUsedShortTermRentalCalculator}
      />
      <AirDnaSavings stateKey={stateKey} locked={locked} />
      {showExtraPayments && <AirDnaExtraPayments stateKey={stateKey} />}
      {corporateProfile && <AirDnaLearnMoreButton />}
    </>
  )
}
